import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { DialogDetail } from "../../components/DialogDetail";
import type { LearnerReport } from "../../types";

const STATUS_LABEL: Record<string, string> = {
  mastered: "Mastered",
  in_progress: "In progress",
  needs_review: "Needs review",
  not_started: "Not started",
};

export function TutorLearnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  const [openSession, setOpenSession] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["tutor-learner", id],
    queryFn: () => apiGet<LearnerReport>(`/api/tutor/learners/${id}`),
  });

  const resetMutation = useMutation({
    mutationFn: (lessonId: string) =>
      apiPost(`/api/tutor/learners/${id}/lessons/${lessonId}/reset-attempts`),
    onSuccess: (_data, lessonId) => {
      setResetNotice(`Attempts reset for ${lessonId}. The learner can try again.`);
      queryClient.invalidateQueries({ queryKey: ["tutor-learner", id] });
    },
    onError: (err: any) => setResetNotice(err?.message || "Reset failed."),
  });

  if (isLoading) return <p className="text-muted">Loading learner report...</p>;
  if (error || !data) return <p className="error-message">Learner not found or access denied.</p>;

  // Unique lessons this learner has attempted (from their dialog history).
  const attemptedLessons = Array.from(new Set(data.dialogs.map((d) => d.lesson_id)));

  return (
    <div className="learner-detail">
      <Link to="/tutor/learners" className="breadcrumb-link">← Back to Roster</Link>
      <h1>{data.name}</h1>
      <p className="text-muted">
        {data.student_number && (
          <>
            <span className="student-number">{data.student_number}</span>
            {" · "}
          </>
        )}
        Overall mastery: <strong>{data.mastery}%</strong>
      </p>

      {resetNotice && <div className="follow-up-hint">{resetNotice}</div>}

      {/* Attempt management — admin only, matching the endpoint. An instructor
          sees the lock in the report but asks an admin to lift it. */}
      {user?.role === "admin" && attemptedLessons.length > 0 && (
        <div className="card">
          <h3>🔓 Reset Lesson Attempts</h3>
          <p className="text-muted">
            A learner is locked out of a lesson after using all their attempts without passing.
            Reset a lesson to give them a fresh set of attempts. Their history is kept.
          </p>
          <table>
            <tbody>
              {attemptedLessons.map((lessonId) => (
                <tr key={lessonId}>
                  <td>{lessonId}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn btn-sm"
                      disabled={resetMutation.isPending}
                      onClick={() => resetMutation.mutate(lessonId)}
                    >
                      {resetMutation.isPending ? "Resetting..." : "Reset attempts"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Objective mastery */}
      <div className="card">
        <h3>📊 Objective Mastery</h3>
        {data.objectives.length === 0 ? (
          <p className="text-muted">No objectives attempted yet.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Subject</th><th>Objective</th><th>Score</th><th>Status</th><th>Attempts</th><th>Trend</th></tr>
            </thead>
            <tbody>
              {data.objectives.map((o, i) => (
                <tr key={i}>
                  <td className="text-muted" style={{ fontSize: "0.82rem" }}>{o.subject ?? "—"}</td>
                  <td>{o.description}</td>
                  <td>
                    <span className={o.score >= 70 ? "text-success" : o.score >= 50 ? "text-warning" : "text-danger"}>
                      {o.score}%
                    </span>
                  </td>
                  <td><span className={`badge badge-${o.status}`}>{STATUS_LABEL[o.status] ?? o.status}</span></td>
                  <td>{o.attempts}</td>
                  <td>{o.trend === "improving" ? "📈" : o.trend === "declining" ? "📉" : "➡️"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent dialog sessions */}
      <div className="card">
        <h3>💬 Recent Dialogs</h3>
        {data.dialogs.length === 0 ? (
          <p className="text-muted">No completed dialog sessions.</p>
        ) : (
          <div className="dialog-history">
            {data.dialogs.map((d, i) => {
              const open = openSession === d.session_id;
              return (
                <div key={i} className="dialog-history-item">
                  <div
                    className="dialog-history-header dialog-history-clickable"
                    onClick={() => setOpenSession(open ? null : d.session_id)}
                    role="button"
                  >
                    <span className="dialog-toggle">{open ? "▾" : "▸"}</span>
                    <span className={`dialog-score ${d.score >= 70 ? "text-success" : d.score >= 50 ? "text-warning" : "text-danger"}`}>
                      {d.score}%
                    </span>
                    {d.subject && <span className="badge">{d.subject}</span>}
                    <span className="dialog-lesson">{d.lesson_title || "Lesson unavailable"}</span>
                    <span className="text-muted">{new Date(d.completed_at).toLocaleDateString()}</span>
                    {d.duration_minutes != null && (
                      <span className="text-muted" title="Time taken">⏱ {d.duration_minutes} min</span>
                    )}
                    {d.ready_to_advance && <span className="badge badge-success">Advanced</span>}
                  </div>
                  {!open && d.misconceptions.length > 0 && (
                    <div className="dialog-misconceptions">
                      <span className="text-muted">Misconceptions: </span>
                      {d.misconceptions.map((m, j) => (
                        <span key={j} className="badge badge-warning">{m}</span>
                      ))}
                    </div>
                  )}
                  {!open && d.calculation_errors.length > 0 && (
                    <div className="dialog-misconceptions">
                      <span className="text-muted">Arithmetic to double-check: </span>
                      {d.calculation_errors.map((c, j) => (
                        <span key={j} className="badge badge-primary">{c}</span>
                      ))}
                    </div>
                  )}
                  {open && <DialogDetail url={`/api/tutor/sessions/${d.session_id}`} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
